import { z } from "zod";

/*
 * Stufe 1 von Angebote, Aufträge und Rechnungen: Belege. Geldbeträge sind immer ganze Cent.
 * Die Berechnung liegt hier, damit Oberfläche und Server dieselben Summen zeigen.
 */

export const DOC_TYPES = ["quote", "order", "invoice", "credit_note"] as const;
export type DocType = (typeof DOC_TYPES)[number];

export const DOC_LABEL: Record<DocType, string> = { quote: "Angebot", order: "Auftrag", invoice: "Rechnung", credit_note: "Gutschrift" };
export const DOC_PLURAL: Record<DocType, string> = { quote: "Angebote", order: "Aufträge", invoice: "Rechnungen", credit_note: "Gutschriften" };
export const DOC_DEFAULT_PREFIX: Record<DocType, string> = { quote: "AN-", order: "AU-", invoice: "RE-", credit_note: "GS-" };
/** Aufbewahrung in Jahren (jeweils bis zum Jahresende): Rechnungen 8, Geschäftsbriefe 6. */
export const DOC_RETENTION_YEARS: Record<DocType, number> = { quote: 6, order: 6, invoice: 8, credit_note: 8 };

export const DOC_STATES = ["draft", "open", "overdue", "paid", "cancelled"] as const;
/** Zustand für die Übersicht: Entwurf, offen, überfällig, bezahlt, storniert (nur Rechnungen kennen offen bis bezahlt). */
export type DocState = (typeof DOC_STATES)[number];
export const STATE_LABEL: Record<DocState, string> = { draft: "Entwurf", open: "Offen", overdue: "Fällig", paid: "Bezahlt", cancelled: "Storniert" };

export const SMALL_BUSINESS_NOTE = "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.";

/** Pflichthinweis bei Leistungen im Zusammenhang mit einem Grundstück an Privatpersonen (§ 14 Abs. 4 Nr. 9 UStG). */
export const RETENTION_NOTE =
  "Hinweis für Privatpersonen: Bei Leistungen im Zusammenhang mit einem Grundstück sind Sie verpflichtet, diese Rechnung zwei Jahre aufzubewahren (§ 14 Abs. 4 Nr. 9 UStG).";

/** Gründe, aus denen eine Rechnung ohne Umsatzsteuer (0 %) ausgestellt werden darf. `code` ist die Steuerkategorie der E-Rechnung (EN 16931). */
export const VAT_REASONS = {
  reverse_charge: { label: "Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG, z. B. Bauleistungen an Unternehmer)", note: "Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG).", code: "AE", needsBuyerVatId: false },
  exempt: { label: "Steuerfreie Leistung (§ 4 UStG)", note: "Steuerfreie Leistung nach § 4 UStG.", code: "E", needsBuyerVatId: false },
  intra_community: { label: "Steuerfreie innergemeinschaftliche Lieferung (EU)", note: "Steuerfreie innergemeinschaftliche Lieferung (§ 4 Nr. 1b i. V. m. § 6a UStG).", code: "K", needsBuyerVatId: true },
  export: { label: "Steuerfreie Ausfuhrlieferung (Drittland)", note: "Steuerfreie Ausfuhrlieferung (§ 4 Nr. 1a i. V. m. § 6 UStG).", code: "G", needsBuyerVatId: false },
  not_taxable: { label: "Nicht steuerbar (z. B. Leistungsort im Ausland)", note: "Nicht im Inland steuerbare Leistung.", code: "O", needsBuyerVatId: false },
} as const;
export type VatReason = keyof typeof VAT_REASONS;
export const VAT_REASON_KEYS = Object.keys(VAT_REASONS) as [VatReason, ...VatReason[]];

/** Lieferbedingungen nach Incoterms® 2020, verständlich erklärt. */
export const INCOTERMS = {
  EXW: { label: "EXW – ab Werk", note: "Der Kunde holt die Ware bei Ihnen ab und trägt ab dann Transport und Risiko." },
  FCA: { label: "FCA – frei Frachtführer", note: "Sie übergeben die Ware am vereinbarten Ort an den Spediteur des Kunden." },
  CPT: { label: "CPT – frachtfrei", note: "Sie bezahlen den Transport bis zum Zielort, das Risiko geht aber schon bei Übergabe an den Spediteur auf den Kunden über." },
  CIP: { label: "CIP – frachtfrei versichert", note: "Wie CPT, zusätzlich schließen Sie eine Transportversicherung ab." },
  DAP: { label: "DAP – geliefert benannter Ort", note: "Sie liefern auf Ihre Kosten und Gefahr bis zum Ort des Kunden, abgeladen wird dort vom Kunden. Üblich bei Lieferung im Inland." },
  DPU: { label: "DPU – geliefert und entladen", note: "Wie DAP, Sie entladen die Ware am Zielort selbst." },
  DDP: { label: "DDP – geliefert verzollt", note: "Sie tragen alle Kosten und Risiken bis zum Kunden, auch Zoll und Einfuhrabgaben." },
} as const;
export type Incoterm = keyof typeof INCOTERMS;
export const INCOTERM_KEYS = Object.keys(INCOTERMS) as [Incoterm, ...Incoterm[]];

/** Vorschläge für die Zahlungsbedingungen (frei änderbar). */
export const PAYMENT_TERM_PRESETS = [
  "Zahlbar innerhalb von 14 Tagen netto ohne Abzug.",
  "Zahlbar innerhalb von 30 Tagen netto ohne Abzug.",
  "Sofort fällig, ohne Abzug.",
  "Zahlbar innerhalb von 14 Tagen netto, bei Zahlung innerhalb von 8 Tagen 2 % Skonto.",
  "50 % Abschlag bei Auftragserteilung, der Rest nach Fertigstellung, jeweils innerhalb von 14 Tagen netto.",
] as const;

const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum angeben.");
const text = (max: number) =>
  z
    .string()
    .max(max)
    .transform((v) => v.trim() || null)
    .nullable()
    .optional()
    .transform((v) => v ?? null);

export const docLineSchema = z.object({
  name: z.string().trim().min(1, "Jede Position braucht eine Bezeichnung.").max(200),
  description: text(2000),
  quantity: z.number().min(-1_000_000).max(1_000_000).refine((n) => n !== 0, "Die Menge darf nicht 0 sein."),
  unit: z.string().trim().min(1).max(20).default("Stk"),
  unitPriceCents: z.number().int().min(-1_000_000_000).max(1_000_000_000),
  vatRate: z.union([z.literal(0), z.literal(7), z.literal(19)]),
  discountPercent: z.number().min(0).max(100).default(0),
  /** Alternativposition: wird angezeigt, zählt aber nicht zur Summe. */
  isAlternative: z.boolean().default(false),
  /** Aufschlag in Prozent auf den Preis nach Rabatt. */
  markupPercent: z.number().min(0).max(500).default(0),
  /** Rabatt und Aufschlag nicht auf dem Beleg ausweisen: Der Kunde sieht nur den fertigen Einzelpreis. */
  hideAdjustment: z.boolean().default(false),
});
export type DocLineInput = z.infer<typeof docLineSchema>;

export const createDocumentSchema = z.object({
  type: z.enum(["quote", "order", "invoice"]),
  customerId: z.uuid(),
  projectId: z.uuid().nullable().optional(),
});

export const documentSchema = z.object({
  customerId: z.uuid().optional(),
  projectId: z.uuid().nullable().optional(),
  issueDate: date,
  serviceFrom: date.nullable().optional(),
  serviceTo: date.nullable().optional(),
  dueDate: date.nullable().optional(),
  subject: text(200),
  intro: text(4000),
  closing: text(4000),
  /** Grund, wenn Positionen ohne Umsatzsteuer (0 %) abgerechnet werden. */
  vatReason: z.enum(VAT_REASON_KEYS).nullable().optional(),
  /** Nur bei Angeboten: Name der Variante, z. B. „Variante A“. */
  variantLabel: z.string().trim().max(60).optional().transform((v) => v || null),
  /** Aufbewahrungshinweis für Privatpersonen (Leistung im Zusammenhang mit einem Grundstück). */
  propertyNotice: z.boolean().optional(),
  reference: text(120),
  incoterm: z.enum(INCOTERM_KEYS).nullable().optional(),
  deliveryNote: text(500),
  paymentTerms: text(500),
  lines: z.array(docLineSchema).max(200),
});
export type DocumentInput = z.infer<typeof documentSchema>;

export const convertSchema = z.object({ to: z.enum(["order", "invoice"]) });

export const paymentSchema = z.object({
  amountCents: z.number().int().refine((n) => n !== 0, "Der Betrag darf nicht 0 sein.").refine((n) => Math.abs(n) <= 1_000_000_000_00),
  paidOn: date,
  method: text(60),
  note: text(300),
});

export const numberingSchema = z.object({
  prefix: z.string().trim().max(12).default(""),
  padding: z.number().int().min(0).max(10),
  /** Nächste Nummer. Darf nur steigen, damit nie eine Nummer doppelt vergeben wird. */
  nextNumber: z.number().int().min(1).max(999_999_999).optional(),
});

export interface DocLineInfo extends DocLineInput {
  netCents: number;
}

export interface DocTotals {
  lineNets: number[];
  netCents: number;
  /** Umsatzsteuer je Satz, nur Sätze mit Positionen. */
  vatByRate: { rate: number; netCents: number; vatCents: number }[];
  vatCents: number;
  grossCents: number;
}

/** Nettobetrag einer Position in Cent, kaufmännisch gerundet (ab 0,5 weg von null), ohne Gleitkomma-Fehler. */
export function lineNetCents(l: Pick<DocLineInput, "quantity" | "unitPriceCents" | "discountPercent"> & { markupPercent?: number }): number {
  const qty = BigInt(Math.round(l.quantity * 1000));
  const disc = BigInt(Math.round((100 - (l.discountPercent ?? 0)) * 100));
  const up = BigInt(Math.round((100 + (l.markupPercent ?? 0)) * 100));
  const num = qty * BigInt(l.unitPriceCents) * disc * up;
  const den = 100_000_000_000n;
  const half = den / 2n;
  const r = num >= 0n ? (num + half) / den : -((-num + half) / den);
  return Number(r);
}

export function calcTotals(
  lines: (Pick<DocLineInput, "quantity" | "unitPriceCents" | "discountPercent" | "vatRate"> & { isAlternative?: boolean; markupPercent?: number })[],
  smallBusiness: boolean,
): DocTotals {
  const lineNets = lines.map(lineNetCents);
  const groups = new Map<number, number>();
  lines.forEach((l, i) => {
    if (l.isAlternative) return;
    const rate = smallBusiness ? 0 : l.vatRate;
    groups.set(rate, (groups.get(rate) ?? 0) + lineNets[i]!);
  });
  const vatByRate = [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rate, netCents]) => {
      const v = BigInt(netCents) * BigInt(Math.round(rate * 100));
      const vatCents = Number(v >= 0n ? (v + 5000n) / 10000n : -((-v + 5000n) / 10000n));
      return { rate, netCents, vatCents };
    });
  const netCents = lineNets.reduce((a, b, i) => a + (lines[i]!.isAlternative ? 0 : b), 0);
  const vatCents = vatByRate.reduce((a, g) => a + g.vatCents, 0);
  return { lineNets, netCents, vatByRate, vatCents, grossCents: netCents + vatCents };
}

/** Nummer als Text, z. B. „RE-4040“. */
export function formatDocNumber(prefix: string, padding: number, n: number): string {
  return `${prefix}${String(n).padStart(padding, "0")}`;
}

/** Ende der Aufbewahrungsfrist: Ende des Jahres, in dem die Frist ausläuft. */
export function retentionUntil(type: DocType, issueDate: string): string {
  return `${Number(issueDate.slice(0, 4)) + DOC_RETENTION_YEARS[type]}-12-31`;
}

export interface DocPartyInfo {
  name: string;
  contactName?: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  vatId?: string | null;
  taxNumber?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  ownerName?: string | null;
  registerInfo?: string | null;
  bankName?: string | null;
  iban?: string | null;
  bic?: string | null;
  customerNumber?: number;
}

export interface PaymentInfo {
  id: string;
  amountCents: number;
  paidOn: string;
  method: string | null;
  note: string | null;
}

export interface DocLogEntry {
  action: string;
  detail: string | null;
  at: string;
}

export const DUNNING_LABEL = ["", "Zahlungserinnerung", "1. Mahnung", "2. Mahnung"] as const;

export const dunningSettingsSchema = z.object({
  fee1Cents: z.number().int().min(0).max(100_000),
  fee2Cents: z.number().int().min(0).max(100_000),
  fee3Cents: z.number().int().min(0).max(100_000),
  days: z.number().int().min(1).max(90),
});
export type DunningSettings = z.infer<typeof dunningSettingsSchema>;

export interface DunningInfo {
  id: string;
  level: number;
  issueDate: string;
  dueDate: string;
  feeCents: number;
  openCents: number;
}

/** Eine überfällige Rechnung in der Mahnübersicht. */
export interface DunningDueItem {
  documentId: string;
  number: string;
  customerName: string;
  dueDate: string;
  daysOverdue: number;
  grossCents: number;
  openCents: number;
  lastLevel: number;
  /** 1 bis 3, null wenn schon zweimal gemahnt wurde. */
  nextLevel: number | null;
  nextFeeCents: number;
}

export interface DocAttachmentInfo {
  id: string;
  name: string;
  mime: string;
  sizeBytes: number;
  createdAt: string;
}

export interface DocumentSummary {
  id: string;
  type: DocType;
  status: "draft" | "final";
  state: DocState;
  number: string | null;
  customerId: string;
  customerName: string;
  customerNumber: number;
  projectId: string | null;
  subject: string | null;
  issueDate: string;
  dueDate: string | null;
  grossCents: number;
  paidCents: number;
  relatedId: string | null;
  relatedNumber: string | null;
  createdAt: string;
  /** Vermerk „versendet am“ (JJJJ-MM-TT), frei setzbar, auch nach dem Festschreiben. */
  sentOn: string | null;
  /** Anzahl angehängter Dateien (für die Büroklammer in der Übersicht). */
  attachmentCount: number;
}

export interface DocumentDetail extends DocumentSummary {
  serviceFrom: string | null;
  serviceTo: string | null;
  intro: string | null;
  closing: string | null;
  reference: string | null;
  vatReason: VatReason | null;
  /** Angebot in Varianten: Gruppe, eigener Name und alle Geschwister (inklusive diesem Angebot). */
  incoterm: Incoterm | null;
  deliveryNote: string | null;
  paymentTerms: string | null;
  variantGroup: string | null;
  variantLabel: string | null;
  variants: { id: string; label: string | null; number: string | null; grossCents: number }[];
  /** null = Beleg aus der Zeit vor dem Schalter: Hinweis nach der alten Regel (Kunde ohne USt-IdNr.). */
  propertyNotice: boolean | null;
  smallBusiness: boolean;
  netCents: number;
  vatCents: number;
  vatByRate: DocTotals["vatByRate"];
  lines: DocLineInfo[];
  payments: PaymentInfo[];
  seller: DocPartyInfo | null;
  buyer: DocPartyInfo | null;
  hash: string | null;
  retentionUntil: string | null;
  finalizedAt: string | null;
  /** Nummer der Gutschrift, falls diese Rechnung storniert wurde. */
  cancelledBy: string | null;
  log: DocLogEntry[];
  dunnings: DunningInfo[];
  attachments: DocAttachmentInfo[];
}

export interface NumberingInfo {
  type: DocType;
  prefix: string;
  padding: number;
  nextNumber: number;
  example: string;
}

export interface DocumentListResponse {
  items: DocumentSummary[];
  /** Summen für die Kopfzeile der Übersicht (nur Rechnungen). */
  openCents: number;
  overdueCents: number;
}

/** Angebote und Aufträge bleiben frei änderbar; nur Rechnungen und Gutschriften werden festgeschrieben. */
export const isFreeDoc = (t: DocType): boolean => t === "quote" || t === "order";

/** Zustand einer Rechnung aus Festschreibung, Zahlungen, Fälligkeit und Stornierung. */
export function docState(p: {
  type: DocType;
  status: "draft" | "final";
  grossCents: number;
  paidCents: number;
  dueDate: string | null;
  cancelled: boolean;
  today: string;
}): DocState {
  // Angebote und Aufträge werden nicht festgeschrieben: sie sind immer „offen“ und frei änderbar.
  if (p.status === "draft") return isFreeDoc(p.type) ? "open" : "draft";
  if (p.cancelled) return "cancelled";
  if (p.type !== "invoice") return "open";
  if (p.paidCents >= p.grossCents) return "paid";
  if (p.dueDate && p.dueDate < p.today) return "overdue";
  return "open";
}

export const documentSendSchema = z.object({
  /** Leer: nur einen Link erzeugen (zum Kopieren oder für WhatsApp), ohne Mail. */
  email: z.string().trim().max(200).optional().transform((v) => v || null),
  message: text(1000),
  days: z.number().int().min(1).max(365).default(30),
  /** PDF an die Mail anhängen (nur bei E-Mail). */
  attachPdf: z.boolean().default(true),
});

export const documentMarksSchema = z.object({ sentOn: date.nullable() });

export interface DocumentShareInfo {
  id: string;
  email: string | null;
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
  lastOpenedAt: string | null;
  downloads: number;
}

/** Antwort beim Erzeugen: der Link steht nur hier, danach ist nur noch ein Hash gespeichert. */
export interface DocumentShareCreated extends DocumentShareInfo {
  url: string;
}

export interface PublicDocument {
  company: string;
  line: string;
  hasLogo: boolean;
  type: DocType;
  number: string;
  issueDate: string;
  dueDate: string | null;
  grossCents: number;
  message: string | null;
  sender: string | null;
  expiresAt: string;
}

export const TEXT_KINDS = ["subject", "intro", "closing"] as const;
export type TextKind = (typeof TEXT_KINDS)[number];
export const TEXT_KIND_LABEL: Record<TextKind, string> = { subject: "Betreff", intro: "Einleitung", closing: "Schlusstext" };

export const textTemplateSchema = z.object({
  kind: z.enum(TEXT_KINDS),
  name: z.string().trim().min(1, "Bitte geben Sie der Vorlage einen Namen.").max(80),
  body: z.string().trim().min(1, "Der Text darf nicht leer sein.").max(4000),
});

export interface TextTemplateInfo {
  id: string;
  kind: TextKind;
  name: string;
  body: string;
}

/** Fertige Texte zum Auswählen. Eigene Vorlagen kommen dazu. */
export const STANDARD_TEXTS: Record<TextKind, { name: string; body: string; types?: DocType[] }[]> = {
  subject: [
    { name: "Angebot", body: "Ihr Angebot", types: ["quote"] },
    { name: "Auftragsbestätigung", body: "Auftragsbestätigung", types: ["order"] },
    { name: "Rechnung", body: "Ihre Rechnung", types: ["invoice"] },
  ],
  intro: [
    { name: "Angebot: Vielen Dank für Ihre Anfrage", body: "Vielen Dank für Ihre Anfrage. Gerne bieten wir Ihnen an:", types: ["quote"] },
    { name: "Angebot: nach Besichtigung", body: "Vielen Dank für das freundliche Gespräch vor Ort. Wie besprochen bieten wir Ihnen an:", types: ["quote"] },
    { name: "Auftrag: Bestätigung", body: "Vielen Dank für Ihren Auftrag. Wir bestätigen Ihnen hiermit folgende Leistungen:", types: ["order"] },
    { name: "Rechnung: Vielen Dank für Ihren Auftrag", body: "Vielen Dank für Ihren Auftrag. Wir berechnen Ihnen:", types: ["invoice"] },
    { name: "Rechnung: Für die ausgeführten Arbeiten", body: "Für die ausgeführten Arbeiten stellen wir Ihnen Folgendes in Rechnung:", types: ["invoice"] },
    { name: "Gutschrift", body: "Wir schreiben Ihnen folgende Beträge gut:", types: ["credit_note"] },
  ],
  closing: [
    { name: "Angebot: Gültigkeit", body: "Dieses Angebot gilt 30 Tage ab Angebotsdatum. Über Ihren Auftrag würden wir uns freuen.\n\nMit freundlichen Grüßen", types: ["quote"] },
    { name: "Angebot: Alternativen", body: "Mit „Alternativ“ gekennzeichnete Positionen sind Vorschläge und nicht in der Summe enthalten. Sagen Sie uns einfach, welche Sie wünschen.\n\nMit freundlichen Grüßen", types: ["quote"] },
    { name: "Auftrag: Schlusstext", body: "Wir freuen uns auf die Zusammenarbeit und melden uns zur Terminabstimmung.\n\nMit freundlichen Grüßen", types: ["order"] },
    { name: "Rechnung: Zahlungsziel", body: "Bitte überweisen Sie den Betrag innerhalb des angegebenen Zahlungsziels unter Angabe der Rechnungsnummer.\n\nMit freundlichen Grüßen", types: ["invoice"] },
    { name: "Rechnung: Vielen Dank", body: "Vielen Dank für das Vertrauen. Bei Fragen zur Rechnung melden Sie sich gern.\n\nMit freundlichen Grüßen", types: ["invoice"] },
    { name: "Mit freundlichen Grüßen", body: "Mit freundlichen Grüßen" },
  ],
};
